// src/components/Screens/Dashboard.jsx
// (drop-in) — faster perceived load: video preloaded + heavy UI deferred

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
  useMemo,
} from "react";
import { useNavigate, Link } from "react-router-dom";
import RGL from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { motion } from "framer-motion";
import {
  Volume2,
  SunMedium,
  MoonStar,
  Home as HomeIcon,
  Users,
  Package,
  Images,
  LayoutTemplate,
  FileText,
  Receipt as ReceiptIcon,
  Grid2X2,
  Sprout,
  CalendarDays,
} from "lucide-react";
import { useFeedback } from "../../contexts/FeedbackContext";
import { useStorage } from "../../contexts/StorageContext";
import { useTheme } from "../../core/theme/ThemeProvider";
import { navigateTo, ROUTES } from "../../lib/nav";
import mudoLogo from "../../assets/mudo-logo-bg.png";
import SoundToggle from "../Controls/SoundToggle";
import StudioStatsRibbon from "../Stats/StudioStatsRibbon";
import VideoBackdrop from "../Decor/VideoBackdrop";
// remove external toolbar; use inline icon row in header
import WidgetShell from "../../ui/WidgetShell";
import Button from "../../ui/Button";

// Inspiration Strip (Pexels-powered)
import InspirationStrip from "../Strips/InspirationStrip";

// Widgets
import CostWidget from "../Widgets/CostWidget";
import MaterialDetailsWidget from "../Widgets/MaterialDetailsWidget";
import ReorderPOWidget from "../Widgets/ReorderPOWidget";
import StaffWidget from "../Widgets/StaffWidget";
import UtilitiesWidget from "../Widgets/UtilitiesWidget";
import KilnTrackerWidget from "../Widgets/KilnTrackerWidget";
import WorkshopsWidget from "../Widgets/WorkshopsWidget";
import PackagingWidget from "../Widgets/PackagingWidget";
import AnalyticsWidget from "../Widgets/AnalyticsWidget";
import MarketingWidget from "../Widgets/MarketingWidget";
import TemplatesWidget from "../Widgets/TemplatesWidget";
import StaffCostWidget from "../Widgets/StaffCostWidget";
import TaskWidget from "../Widgets/TaskWidget";
import ParticipantsWidget from "../Widgets/ParticipantsWidget";
import ReceiptsWidget from "../Widgets/ReceiptsWidget";
import TinyBadgeWidget from "../Widgets/TinyBadgeWidget";
import ProductionWidget from "../Widgets/ProductionWidget";
import DailyTodoWidget from "../Widgets/DailyTodoWidget";
import JournalWidget from "../Widgets/JournalWidget";
import MarketsChecklistWidget from "../Widgets/MarketsChecklistWidget";
import StreakWidget from "../Widgets/StreakWidget";
import StudioSpaceWidget from "../Widgets/StudioSpaceWidget"; // Studio booking
import MudoMapWidget from "../Widgets/MudoMapWidget"; // Global studio map

// Modals & Tools
import StaffModal from "../Modals/StaffModal";
import UtilitiesModal from "../Modals/UtilitiesModal";
import KilnLogModal from "../Modals/KilnLogModal";
import AdvancedCalculator from "../Costplanner/AdvancedCalculator";
// Dev helper
// Seed feature removed

import { useStaff } from "../../contexts/StaffContext";
import { StaffContext } from "../../contexts/StaffContext";

function PlaceholderCard({ title, note }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-4">
      <div className="text-sm font-semibold">{title}</div>
      <div className="text-xs opacity-70 mt-1">{note || "Coming soon."}</div>
    </div>
  );
}

const ReactGridLayout = RGL;
const COLS = 3;
const ROW_HEIGHT = 30;

// WIDGET_MAP — includes StudioSpaces key
const WIDGET_MAP = {
  CostPlanner: CostWidget,
  MaterialDetails: MaterialDetailsWidget,
  ReorderPO: ReorderPOWidget,
  Staff: StaffWidget,
  Utilities: UtilitiesWidget,
  KilnTracker: KilnTrackerWidget,
  Workshops: WorkshopsWidget,
  Packaging: PackagingWidget,
  Analytics: AnalyticsWidget,
  Marketing: MarketingWidget,
  Templates: TemplatesWidget,
  StaffCost: StaffCostWidget,
  Tasks: TaskWidget,
  Participants: ParticipantsWidget,
  Receipts: ReceiptsWidget,
  TinyBadge: TinyBadgeWidget,
  Production: ProductionWidget,
  DailyTodo: DailyTodoWidget,
  Journal: JournalWidget,
  MarketsChecklist: MarketsChecklistWidget,
  Streak: StreakWidget,
  // NEW
  StudioSpaces: StudioSpaceWidget,
  MudoMap: MudoMapWidget,
};

/** Locked set aligned with ManageWidgetsModal */
const LOCKED_ORDER = [
  "CostPlanner",
  "MudoMap",
  "DailyTodo",
  "Streak",
  "Journal",
  "Tasks",
  "Analytics",
  "MaterialDetails",
  "Staff",
  "Utilities",
  "Packaging",
  "Marketing",
];
const LOCKED = new Set(LOCKED_ORDER);

/** Widgets the user can toggle (exclude locked) */
const TOGGLEABLE_WIDGETS = [
  "ReorderPO",
  "KilnTracker",
  "Workshops",
  "Templates",
  "StaffCost",
  "Participants",
  "Receipts",
  "TinyBadge",
  "Production",
  "MarketsChecklist",
  // Correct key for booking widget
  "StudioSpaces",
  "MudoMap",
];

/* ---------- Theme → primary color resolver (for drag placeholder) ---------- */
const THEME_PRIMARY = {
  "Burnt Banksia": "#f97316",
  "Red Earth Clay": "#e11d48",
  "Stormy Gum": "#0ea5e9",
  "Basalt Ridge": "#6366f1",
  "Midnight Wattle": "#22c55e",
  "Blackwood Bark": "#f59e0b",
};
function resolvePrimary(settings, isDark) {
  if (settings?.primaryColor) return settings.primaryColor; // explicit override
  if (settings?.themeName && THEME_PRIMARY[settings.themeName]) {
    return THEME_PRIMARY[settings.themeName];
  }
  // sensible default if theme unknown
  return isDark ? "#f59e0b" : "#ea580c";
}

/* ---------------- Error Boundary ---------------- */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 rounded-lg border border-red-500/30 bg-red-500/10 text-red-200">
          <div className="font-semibold mb-1">Something went wrong.</div>
          <div className="text-xs opacity-80 mb-3">
            {String(this.state.error?.message || "Unknown error")}
          </div>
          <button
            className="px-3 py-1 rounded bg-red-600 text-white text-sm"
            onClick={() => window.location.reload()}
          >
            Try reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ------- Auto-measuring wrapper ------ */
function MeasuredWidget({ id, onResize, children }) {
  const ref = useRef(null);
  const rafId = useRef(null);

  useEffect(() => {
    let ro;

    const measureNow = () => {
      if (!ref.current || !ref.current.getBoundingClientRect) return;
      const rect = ref.current.getBoundingClientRect();
      const rows = Math.max(1, Math.ceil(rect.height / ROW_HEIGHT));
      onResize(id, rows);
    };

    const schedule = () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(measureNow);
    };

    schedule();

    if (ref.current && "ResizeObserver" in window) {
      ro = new ResizeObserver(schedule);
      ro.observe(ref.current);
    }

    const onWinResize = () => schedule();
    window.addEventListener("resize", onWinResize);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", onWinResize);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [id, onResize]);

  return <div ref={ref}>{children}</div>;
}

/* ---- Motion presets ---- */
const innerMotion = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
  whileHover: { y: -2, scale: 1.01, filter: "brightness(1.03)" },
  whileTap: { scale: 0.995 },
};

/* ---------- layout helpers ---------- */
function sanitizeLayout(layout, allowedKeys) {
  const allow = new Set(allowedKeys);
  return (layout || []).filter((it) => allow.has(it.i));
}
function mergeLayoutWithKeys(prevLayout, keys) {
  const next = [...sanitizeLayout(prevLayout, keys)];
  const have = new Set(next.map((it) => it.i));
  keys.forEach((k, idx) => {
    if (!have.has(k)) {
      next.push({
        i: k,
        x: idx % COLS,
        y: Infinity,
        w: 1,
        h: 1,
      });
    }
  });
  return next;
}

// ---- AUD currency helper
function currencyAUD(n) {
  const v = Number(n) || 0;
  try {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 2,
    }).format(v);
  } catch {
    return `A$${v.toFixed(2)}`;
  }
}

export default function Dashboard({
  settings,
  updateSettings,
  onHome,
  onOpenGallery,
  onManageWidgets,
  onOpenMaterials,
  onProductsChange,
  onOpenCreateBatch,
  hideInternalHeader,
  // NEW: share planner tasks state
  tasks,
  setTasks,
}) {
  const navigate = useNavigate();
  const { staffList } = useContext(StaffContext);
  const { mode, setMode } = useTheme();
  const isDark = mode === "dark";

  // keep --mudo-primary in sync with theme + mode
  const primary = useMemo(() => resolvePrimary(settings, isDark), [settings, isDark]);
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--mudo-primary", primary);
    root.style.setProperty("--mudo-primary-contrast", isDark ? "#ffffff" : "#111111");
  }, [primary, isDark]);

  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [showUtilitiesModal, setShowUtilitiesModal] = useState(false);
  const [showKilnModal, setShowKilnModal] = useState(false);
  const [calcModalOpen, setCalcModalOpen] = useState(false);
  const [calcProduct, setCalcProduct] = useState(null);
  // Seed feature removed

  // Inspiration search state (persisted)
  const [inspQuery, setInspQuery] = useState(
    () => localStorage.getItem("mudo:inspQuery") || "minimal ceramics"
  );
  useEffect(() => {
    try {
      localStorage.setItem("mudo:inspQuery", inspQuery);
    } catch {}
  }, [inspQuery]);

  // Defer heavy UI until background video can play (removed <link rel="preload"> to avoid unsupported `as` warnings)
  const [bgReady, setBgReady] = useState(false);

  useEffect(() => {
    const v = document.getElementById("mudo-dash-video");
    const onReady = () => setBgReady(true);
    if (v) {
      v.addEventListener("canplaythrough", onReady, { once: true });
      const t = setTimeout(() => setBgReady((ok) => ok || true), 900);
      return () => {
        v.removeEventListener("canplaythrough", onReady);
        clearTimeout(t);
      };
    } else {
      const t = setTimeout(() => setBgReady(true), 700);
      return () => {
        clearTimeout(t);
      };
    }
  }, []);

  // Storage context for workspace-backed data
  const { hasFolder, isDriveLinked, isDriveAuthed, readJSON } = useStorage();
  const hasWorkspace = !!hasFolder || (!!isDriveLinked && !!isDriveAuthed);
  const loadedDesignTemplatesRef = useRef(false);

  // Templates (LS-backed)
  const [templates, setTemplates] = useState([]);
  const loadTemplates = useCallback(() => {
    try {
      const t = JSON.parse(localStorage.getItem("mudoTemplates") || "[]");
      setTemplates(Array.isArray(t) ? t : []);
    } catch {
      setTemplates([]);
    }
  }, []);

  // Design Templates (Workspace-backed) - for visual templates from Canva
  const [designTemplates, setDesignTemplates] = useState([]);
  
  useEffect(() => {
    let alive = true;
    loadedDesignTemplatesRef.current = false;

    (async () => {
      if (!hasWorkspace) {
        if (alive) setDesignTemplates([]);
        loadedDesignTemplatesRef.current = true;
        return;
      }
      try {
        const disk = await readJSON("widgets/templates.json", []);
        if (!alive) return;
        setDesignTemplates(Array.isArray(disk) ? disk : []);
      } catch {
        if (!alive) return;
        setDesignTemplates([]);
      } finally {
        loadedDesignTemplatesRef.current = true;
      }
    })();

    return () => { alive = false; };
  }, [hasWorkspace, readJSON]);

  // Listen for workspace disconnect and template updates
  useEffect(() => {
    const onDisconnect = () => {
      setDesignTemplates([]);
      loadedDesignTemplatesRef.current = false;
    };
    
    const onTemplatesUpdate = async () => {
      if (!hasWorkspace) return;
      try {
        const disk = await readJSON("widgets/templates.json", []);
        setDesignTemplates(Array.isArray(disk) ? disk : []);
      } catch {}
    };
    
    window.addEventListener("mudo:workspace-disconnected", onDisconnect);
    window.addEventListener("mudo:templates-updated", onTemplatesUpdate);
    return () => {
      window.removeEventListener("mudo:workspace-disconnected", onDisconnect);
      window.removeEventListener("mudo:templates-updated", onTemplatesUpdate);
    };
  }, [hasWorkspace, readJSON]);

  // Batches (LS-backed)
  const [batches, setBatches] = useState([]);
  const loadBatches = useCallback(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("mudoBatches") || "[]");
      setBatches(Array.isArray(raw) ? raw : []);
    } catch {
      setBatches([]);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
    loadBatches();
    const onStorage = (e) => {
      if (e.key === "mudoTemplates") loadTemplates();
      if (e.key === "mudoBatches") loadBatches();
    };
    const onCustom = () => {
      loadTemplates();
      loadBatches();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("mudo:batches-updated", onCustom);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("mudo:batches-updated", onCustom);
    };
  }, [loadTemplates, loadBatches]);

  /** CUSTOM widgets only (modal now saves only non-locked) */
  const userSelected = useMemo(
    () =>
      Array.isArray(settings.widgets)
        ? settings.widgets.filter((k) => TOGGLEABLE_WIDGETS.includes(k) && !LOCKED.has(k))
        : [],
    [settings.widgets]
  );

  /** Final render list = LOCKED first (fixed order) + custom */
  const activeWidgetKeys = useMemo(
    () => Array.from(new Set([...LOCKED_ORDER, ...userSelected])).filter((k) => WIDGET_MAP[k]),
    [userSelected]
  );

  const initialLayout = useMemo(() => {
    const saved = settings.widgetsLayout || [];
    return mergeLayoutWithKeys(saved, activeWidgetKeys);
  }, [settings.widgetsLayout, activeWidgetKeys]);
  const [layout, setLayout] = useState(initialLayout);

  // Guard against the grid measuring while hidden (occasionally rendered at 0px width)
  // by measuring the container ourselves and passing an explicit width to RGL.
  const gridWrapRef = useRef(null);
  const [gridWidth, setGridWidth] = useState(0);

  useEffect(() => {
    if (!bgReady) return;
    const node = gridWrapRef.current;
    if (!node) return;

    const measure = () => {
      const w = node.getBoundingClientRect?.().width || node.clientWidth || 0;
      setGridWidth((prev) => (prev !== w ? w : prev));
    };

    measure();

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(node);
    }

    window.addEventListener("resize", measure);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [bgReady]);

  // Occasionally ensure a re-measure after widgets change (covers cases where width was 0)
  useEffect(() => {
    if (!bgReady) return;
    const t = setTimeout(() => {
      const node = gridWrapRef.current;
      if (!node) return;
      const w = node.getBoundingClientRect?.().width || node.clientWidth || 0;
      if (w) setGridWidth((prev) => (prev !== w ? w : prev));
    }, 60);
    return () => clearTimeout(t);
  }, [activeWidgetKeys, bgReady]);

  // Debounce saving layout (smoother drag/resize)
  useEffect(() => {
    const t = setTimeout(() => {
      updateSettings((prev) => ({ ...prev, widgetsLayout: layout }));
    }, 250);
    return () => clearTimeout(t);
  }, [layout, updateSettings]);

  useEffect(() => {
    setLayout((prev) => mergeLayoutWithKeys(prev, activeWidgetKeys));
  }, [activeWidgetKeys]);

  const handleResize = useCallback((id, newH) => {
    setLayout((prev) => {
      let changed = false;
      const updated = prev.map((w) =>
        w.i === id && w.h !== newH ? ((changed = true), { ...w, h: newH }) : w
      );
      return changed ? updated : prev;
    });
  }, []);

  // single feedback hook; avoid duplicate buzz variable
  const { play: p, buzz: hapticBuzz } = useFeedback();

  const withFeedback = (fn, type = "tap", haptic = "light") => () => {
    p(type);
    if (hapticBuzz) hapticBuzz(haptic);
    fn?.();
  };

  const openCalculator = withFeedback(() => {
    setCalcProduct({
      id: Date.now().toString(),
      name: "",
      category: "",
      quantity: 1,
    });
    setCalcModalOpen(true);
  });

  const { addStaff: addS, updateStaff: updS } = useStaff();
  const handleAddStaff = withFeedback(() => {
    setEditingStaff(null);
    setStaffModalOpen(true);
  });
  const handleEditStaff = (staff) =>
    withFeedback(() => {
      setEditingStaff(staff);
      setStaffModalOpen(true);
    })();

  const handleSaveStaff = (data) => {
    try {
      if (editingStaff) updS?.({ ...data, id: editingStaff.id });
      else addS?.(data);
      p("success");
      if (hapticBuzz) hapticBuzz("success");
    } catch {
      p("error");
      if (hapticBuzz) hapticBuzz("error");
    } finally {
      setStaffModalOpen(false);
    }
  };

  const toggleTheme = withFeedback(
    () => setMode(isDark ? "light" : "dark"),
    "tap",
    "light"
  );

  const navItems = [
    { label: "Home", onClick: () => onHome?.() },
    { label: "Workshops", onClick: () => navigateTo(navigate, ROUTES.WORKSHOPS, { source: 'Dashboard:nav' }) },
    { label: "Participants", onClick: () => navigateTo(navigate, ROUTES.PARTICIPANTS, { source: 'Dashboard:nav' }) },
    { label: "Track My Pieces", onClick: () => navigateTo(navigate, ROUTES.MY_PIECES, { source: 'Dashboard:nav' }) },
    { label: "Gallery", onClick: () => onOpenGallery?.() },
    { label: "Templates", onClick: () => navigateTo(navigate, ROUTES.TEMPLATES, { source: 'Dashboard:nav' }) },
    { label: "POs", onClick: () => navigateTo(navigate, ROUTES.PURCHASE_ORDERS, { source: 'Dashboard:nav' }) },
    { label: "Receipts", onClick: () => navigateTo(navigate, ROUTES.RECEIPTS, { source: 'Dashboard:nav' }) },
    { label: "Widgets", onClick: () => onManageWidgets?.() },
    // Seed feature removed
  ].map((item) => ({ ...item, onClick: withFeedback(item.onClick) }));

  const productionPct = useMemo(() => {
    const target = Number(settings.productionOutput) || 0;
    const done = (batches || []).filter((b) => b.status === "Completed").length;
    return target > 0 ? Math.min(100, Math.round((done / target) * 100)) : 0;
  }, [batches, settings.productionOutput]);

  // Active-only staff aggregates (used in header + StaffCostWidget)
  const staffAgg = useMemo(() => {
    const all = Array.isArray(staffList) ? staffList : [];
    const active = all.filter((s) => s?.isActive !== false);
    const weeklyHours = active.reduce((a, s) => a + (Number(s.hoursPerWeek) || 0), 0);
    const weeklyCost = active.reduce(
      (a, s) => a + (Number(s.hourlyRate) || 0) * (Number(s.hoursPerWeek) || 0),
      0
    );
    return {
      activeCount: active.length,
      totalCount: all.length,
      weeklyHours,
      weeklyCost,
      activeList: active,
    };
  }, [staffList]);

  return (
    <>
      {/* Full-bleed video background — expose id for readiness listener */}
      <VideoBackdrop
        videoId="mudo-dash-video"
        preload="auto"
        srcWebm="/video/dashboard-bg.webm"
        srcMp4="/video/dashboard-bg.mp4"
        poster="/video/dashboard-bg.jpg"
        overlayOpacity={0.5}
        overlayColor="black"
        fadeMs={600}
        fadeDelayMs={80}
      />

      {/* Page content sits above backdrop */}
      <div
        className={`relative z-10 min-h-screen p-6 space-y-6 ${isDark ? "text-white" : "text-neutral-900"}`}
        style={{ transition: "color 200ms" }}
      >
  {/* Theme-aware RGL placeholder color */}
        <style>{`
          .react-grid-item.react-grid-placeholder {
            background: var(--mudo-primary, #f59e0b) !important;
            opacity: 0.35 !important;
            border-radius: 12px !important;
            transition: background .2s ease;
          }
          .react-resizable-handle::after {
            border-color: var(--mudo-primary, #f59e0b) !important;
            opacity: .7;
          }
        `}</style>

        {/* Top header — renders immediately for fast first paint */}
        <div
          className="flex flex-wrap items-center justify-between p-4 rounded-lg shadow mb-6"
          style={{
            background: 'var(--mudo-bg-soft)',
            border: '1px solid var(--mudo-border)',
            color: 'var(--mudo-text)',
            backdropFilter: 'blur(6px)',
          }}
        >
          <div className="flex items-center gap-4">
            <img src={mudoLogo} alt="Mudo Logo" className="w-16 h-16" />
            <div>
              <h1 className="text-2xl font-bold">Mudo Dashboard</h1>
              <p className="text-xs opacity-70">
                {settings.businessName || "Your Studio"} • {settings.productionOutput || 0} units/month
              </p>

              {/* Status badges */}
              <div className="mt-1 flex flex-wrap gap-2">
                {settings.productionOutput > 0 && (
                  <span className="px-2 py-1 rounded bg-emerald-600/80 text-white text-xs font-medium">
                    Production: {productionPct}% complete
                  </span>
                )}
                {/* Active-only staff summary (clickable to Staff Targets) */}
                <button
                  onClick={() => navigateTo(navigate, ROUTES.SETTINGS_STAFF_TARGETS, { source: 'Dashboard:staff-badge' })}
                  className="px-2 py-1 rounded bg-blue-600/85 text-white text-xs font-medium hover:brightness-110"
                  title="Open Staff Targets"
                >
                  Active staff: {staffAgg.activeCount}/{staffAgg.totalCount} •{" "}
                  {currencyAUD(staffAgg.weeklyCost)}/wk
                </button>
              </div>
            </div>
          </div>

          {/* Kokonut-style header icon row (preserve handlers) */}
          {(() => {
            const baseBtn =
              "inline-flex items-center justify-center rounded-full h-8 w-8 " +
              "border border-[var(--mudo-border)]/80 bg-[var(--mudo-bg-soft)]/90 " +
              "text-[var(--mudo-text-muted)] hover:text-[var(--mudo-text)] " +
              "hover:bg-[var(--mudo-bg-soft)] focus:outline-none focus:ring-2 " +
              "focus:ring-[var(--mudo-primary)]/75 transition";
            const primaryBtn =
              "inline-flex items-center justify-center rounded-full h-8 w-8 " +
              "border border-[var(--mudo-primary)] bg-[var(--mudo-primary)] text-[var(--mudo-onPrimary,#0b0b0b)] " +
              "hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[var(--mudo-primary)]/75 transition";
            const iconSize = "h-3.5 w-3.5";
            return (
              <div className="flex items-center gap-1.5">
                {/* Sound — keep existing component/handler */}
                <SoundToggle />

                {/* Light theme toggle — keep existing handler */}
                {/* Theme toggle slider — keep existing handler */}
                <button
                  type="button"
                  onClick={toggleTheme}
                  className={"relative inline-flex items-center rounded-full h-7 w-12 border border-[var(--mudo-border)]/80 bg-[var(--mudo-bg-soft)]/80 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--mudo-primary)]/75 transition"}
                  title={isDark ? "Dark mode" : "Light mode"}
                >
                  <span
                    className={
                      "absolute left-1 h-5 w-5 rounded-full bg-[var(--mudo-bg)]/95 flex items-center justify-center text-[var(--mudo-text-muted)] text-[11px] transition-transform " +
                      (isDark ? "" : "translate-x-5")
                    }
                  >
                    {isDark ? (
                      <MoonStar className={iconSize} />
                    ) : (
                      <SunMedium className={iconSize} />
                    )}
                  </span>
                </button>

                {/* Nav items — keep SAME onClick handlers, icon-only */}
                {navItems.map(({ label, onClick }) => {
                  const title = label;
                  const iconMap = {
                    Home: <HomeIcon className={iconSize} />,
                    Workshops: <CalendarDays className={iconSize} />,
                    Participants: <Users className={iconSize} />,
                    "Track My Pieces": <Package className={iconSize} />,
                    Gallery: <Images className={iconSize} />,
                    Templates: <LayoutTemplate className={iconSize} />,
                    POs: <FileText className={iconSize} />,
                    Receipts: <ReceiptIcon className={iconSize} />,
                    Widgets: <Grid2X2 className={iconSize} />,
                    Seed: <Sprout className={iconSize} />,
                  };
                  const icon = iconMap[label] || <Grid2X2 className={iconSize} />;
                  const isWidgets = label === "Widgets";
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={onClick}
                      className={isWidgets ? "inline-flex items-center justify-center rounded-full h-8 w-8 border border-[var(--mudo-primary)] bg-[var(--mudo-primary)] text-[var(--mudo-onPrimary,#0b0b0b)] hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[var(--mudo-primary)]/75 transition" : baseBtn}
                      title={title}
                    >
                      {icon}
                    </button>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* Studio Stats / Story ribbon */}
        <StudioStatsRibbon
          isDark={isDark}
          batches={batches}
          className="-mt-2"
          showCoachCard
        />

  {/* Inspiration header + search pill */}
        <div className="flex flex-col gap-2 -mt-2">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold opacity-90">Inspiration</div>
            <Link
              to={ROUTES.INSPIRATION}
              className="text-xs px-2 py-1 rounded-full bg-white/70 backdrop-blur border border-white/20 shadow hover:bg-white text-black"
            >
              Saved
            </Link>
          </div>

          <div
            className="flex items-center gap-2 p-2 rounded-full shadow"
            style={{
              background: "rgba(255,255,255,0.70)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              border: "1px solid rgba(0,0,0,0.08)",
            }}
          >
            <span className="text-xs px-2 py-1 rounded-full bg-black/10">Search</span>
            <input
              value={inspQuery}
              onChange={(e) => setInspQuery(e.target.value)}
              aria-label="Search inspiration images"
              placeholder={`Try: "minimal ceramics", "glaze drip", "wheel throwing"`}
              className="flex-1 bg-transparent outline-none text-sm placeholder-black/50"
            />
            <button
              onClick={() => setInspQuery((s) => s.trim() || "minimal ceramics")}
              className="text-xs px-3 py-1 rounded-full bg-black/80 text-white"
              title="Run search"
            >
              Go
            </button>
          </div>
        </div>

  {/* Inspiration Strip — mount after bg video is ready (or short timeout) */}
        {bgReady ? (
          <InspirationStrip query={inspQuery} />
        ) : (
          <div className="h-[300px] rounded-lg border border-white/10 bg-white/10 animate-pulse" />
        )}

        {/* Logo strip */}
        <motion.div
          className="flex justify-center mb-6"
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35, ease: "easeOut", delay: 0.05 }}
        >
          {settings.logoUrl ? (
            <img
              src={settings.logoUrl}
              alt="Studio Logo"
              className="rounded object-contain"
              style={{ width: "20vw", maxWidth: "160px", minWidth: "100px" }}
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          ) : (
            <div
              className={`${isDark ? "bg-white/10" : "bg-black/10"} flex items-center justify-center rounded`}
              style={{ width: "20vw", maxWidth: "160px", minWidth: "100px", aspectRatio: "1 / 1" }}
            >
              Logo
            </div>
          )}
        </motion.div>

        {/* Grid — also deferred until the background is ready to keep main thread free */}
        <div ref={gridWrapRef}>
          {bgReady && gridWidth > 0 ? (
            <ReactGridLayout
              layout={layout}
              width={gridWidth}
              cols={COLS}
              rowHeight={ROW_HEIGHT}
              onLayoutChange={setLayout}
              draggableHandle=".drag-handle"
              margin={[16, 16]}
              containerPadding={[16, 16]}
              compactType="vertical"
              verticalCompact={true}
              useCSSTransforms={false}
            >
              {activeWidgetKeys.map((key) => {
                const Widget = WIDGET_MAP[key] || (() => <PlaceholderCard title={key} />);
                let extraProps = {};

                if (key === "CostPlanner") {
                  extraProps = {
                    onCreateNew: openCalculator,
                    onManageProducts: withFeedback(onOpenGallery),
                  };
                } else if (key === "Staff") {
                  extraProps = {
                    onOpenModal: handleAddStaff,
                    onEditStaff: handleEditStaff,
                  };
                } else if (key === "Utilities") {
                  extraProps = {
                    onOpenModal: withFeedback(() => setShowUtilitiesModal(true)),
                    expectedUnits: settings.productionOutput,
                    onExpectedUnitsChange: (n) =>
                      updateSettings((s) => ({ ...s, productionOutput: n })),
                  };
                } else if (key === "KilnTracker") {
                  extraProps = {
                    isDark,
                    onOpenModal: withFeedback(() => setShowKilnModal(true)),
                  };
                } else if (key === "Workshops") {
                  extraProps = { onOpen: withFeedback(() => navigateTo(navigate, ROUTES.WORKSHOPS, { source: 'Dashboard:widget-Workshops' })), isDark };
                } else if (key === "Analytics") {
                  extraProps = { settings };
                } else if (key === "Templates") {
                  extraProps = {
                    templates: designTemplates,
                    backTo: ROUTES.TEMPLATES,
                    onSelect: (tpl) => {
                      // For design templates, we don't load them into calculator
                      // Just navigate to templates page
                      navigateTo(navigate, ROUTES.TEMPLATES, { source: 'Dashboard:widget-Templates' });
                    },
                  };
                } else if (key === "StaffCost") {
                  // Feed only active staff to match header numbers
                  extraProps = {
                    staff: staffAgg.activeList,
                    monthlyOutput: Number(settings.productionOutput || 0) || 1,
                  };
                } else if (key === "Receipts") {
                  extraProps = { isDark };
                } else if (key === "Production") {
                  extraProps = {
                    batches,
                    monthlyTarget: Number(settings.productionOutput || 0) || 0,
                    onCreateBatch: withFeedback(() => {
                      if (onOpenCreateBatch) onOpenCreateBatch();
                      else navigateTo(navigate, ROUTES.PLANNER, { source: 'Dashboard:widget-Production-create' });
                    }),
                    onViewAll: withFeedback(() => navigateTo(navigate, ROUTES.BATCHES, { source: 'Dashboard:widget-Production-viewAll' })),
                    backTo: ROUTES.DASHBOARD,
                  };
                } else if (key === "Tasks") {
                  extraProps = { tasks, setTasks };
                }

                return (
                  <div
                    key={key}
                    className="focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-orange-400"
                  >
                    <motion.div
                      initial={innerMotion.initial}
                      animate={innerMotion.animate}
                      whileHover={innerMotion.whileHover}
                      whileTap={innerMotion.whileTap}
                    >
                      <WidgetShell
                        className="h-full"
                        actions={(
                          <div
                            className="drag-handle p-2 cursor-move select-none flex flex-col gap-1 items-start text-[var(--mudo-text-muted)]"
                            style={{ opacity: 0.65, transition: "opacity 120ms" }}
                            onMouseEnter={(e) => (e.currentTarget.style.opacity = 0.9)}
                            onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.65)}
                            aria-label={`Drag to move ${key} widget`}
                            title="Drag to move"
                          >
                            <div style={{ width: 20, height: 2, background: "currentColor", borderRadius: 1 }} />
                            <div style={{ width: 20, height: 2, background: "currentColor", borderRadius: 1 }} />
                          </div>
                        )}
                      >
                        <MeasuredWidget id={key} onResize={handleResize}>
                          <ErrorBoundary>
                            <Widget {...extraProps} />
                          </ErrorBoundary>
                        </MeasuredWidget>
                      </WidgetShell>
                    </motion.div>
                  </div>
                );
              })}
            </ReactGridLayout>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="h-32 rounded-lg border border-white/10 bg-white/10 animate-pulse"
                />
              ))}
            </div>
          )}
        </div>

        {/* Staff modal */}
        {staffModalOpen && (
          <StaffModal
            initialStaff={editingStaff}
            onClose={withFeedback(() => setStaffModalOpen(false))}
            onSave={handleSaveStaff}
          />
        )}

        {/* Utilities modal */}
        {showUtilitiesModal && (
          <UtilitiesModal
            isOpen={showUtilitiesModal}
            onClose={withFeedback(() => setShowUtilitiesModal(false))}
          />
        )}

        {/* Kiln log modal */}
        {showKilnModal && (
          <KilnLogModal onClose={withFeedback(() => setShowKilnModal(false))} />
        )}

        {/* Advanced calculator modal */}
        {calcModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div
              className={`rounded-lg w-full max-w-5xl h-full max-h-[90vh] overflow-y-auto shadow-lg ${
                isDark ? "bg-zinc-900 text-white" : "bg-white text-zinc-900"
              }`}
            >
              <AdvancedCalculator
                product={calcProduct}
                settings={settings}
                updateProduct={setCalcProduct}
                onSaveProduct={(prod) => {
                  const safeProd = {
                    ...prod,
                    id: prod.id || Date.now().toString(),
                    name: (prod.name || "Unnamed Product").trim(),
                  };
                  try {
                    const existing = JSON.parse(localStorage.getItem("mudoProducts") || "[]");
                    const idx = existing.findIndex(
                      (p) => String(p.id) === String(safeProd.id)
                    );
                    const updated =
                      idx >= 0
                        ? existing.map((p) =>
                            String(p.id) === String(safeProd.id) ? safeProd : p
                          )
                        : [...existing, safeProd];
                    localStorage.setItem("mudoProducts", JSON.stringify(updated));
                    onProductsChange?.(updated);
                    p("success");
                    if (hapticBuzz) hapticBuzz("success");
                  } catch (e) {
                    console.error("[Products save] failed:", e);
                    p("error");
                    if (hapticBuzz) hapticBuzz("error");
                  }
                  setCalcModalOpen(false);
                }}
                onBack={withFeedback(() => setCalcModalOpen(false))}
              />
            </div>
          </div>
        )}

        {/* Seed panel removed */}
      </div>
    </>
  );
}
